/**
 * Velocity tab — pipeline velocity metrics (stage SLAs, time-to-commit).
 * Placeholder wired to eventually pull `dashboard.slaByStage` from `/v3/metrics/dashboard`.
 */

import { useEffect, useState } from 'react';
import { KpiTile, Pill } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import type { SlaByStageRow } from '@retail-ops/shared-types';
import type { RrmFilters } from './types';

export interface VelocityTabProps {
  filters: RrmFilters;
}

export function VelocityTab({ filters }: VelocityTabProps): JSX.Element {
  const client = useApiClient();
  const [loading, setLoading] = useState<boolean>(true);
  const [slaByStage, setSlaByStage] = useState<ReadonlyArray<SlaByStageRow>>([]);

  useEffect(() => {
    const ctrl = new AbortController();
    const days = filters.period === 'week' ? 7 : filters.period === 'quarter' ? 90 : 30;
    client.retail.metrics
      .dashboard({ days }, { signal: ctrl.signal })
      .then((r) => {
        if (r.success) {
          setSlaByStage(r.slaByStage);
        }
      })
      .catch(() => { /* silent — fall back to placeholder */ })
      .finally(() => setLoading(false));
    return () => ctrl.abort();
  }, [client, filters.period]);

  return (
    <>
      <div className="rrm-kpi-strip">
        <KpiTile label="Avg time to commit" value="2h 08m" delta={{ dir: 'down', text: '14m MoM' }} />
        <KpiTile label="P90 stage SLA" value="12m" severity="warn" delta={{ dir: 'up', text: '3m MoM' }} />
        <KpiTile label="Abandon rate" value="4.2%" severity="urgent" delta={{ dir: 'up', text: '0.8pp MoM' }} />
        <KpiTile label="Rework rate" value="8%" severity="approvals" delta={{ dir: 'down', text: '2pp MoM' }} />
      </div>

      <article className="rrm-chart-card">
        <header className="rrm-chart-header">
          <div>
            <div className="rrm-chart-title">Pipeline stage SLAs</div>
            <div className="rrm-chart-sub">Live — `/v3/metrics/dashboard` slaByStage</div>
          </div>
        </header>
        <div className="rrm-chart-body" style={{ padding: 0 }}>
          {loading ? (
            <div style={{ padding: 'var(--sp-9)', textAlign: 'center', color: 'var(--ink-600)' }}>Loading stage SLAs…</div>
          ) : slaByStage.length === 0 ? (
            <div style={{ padding: 'var(--sp-9)', color: 'var(--ink-600)' }}>No stage SLA data yet. Populated when `/v3/metrics/dashboard` returns data.</div>
          ) : (
            <table className="grid-table" aria-label="Stage SLAs">
              <thead>
                <tr>
                  <th>Stage</th>
                  <th>Avg time</th>
                  <th>P90</th>
                  <th>Breaches</th>
                  <th>On-time %</th>
                </tr>
              </thead>
              <tbody>
                {slaByStage.map((s) => (
                  <tr key={s.stage}>
                    <td>{s.stage}</td>
                    <td>{Math.round(s.avgSeconds / 60)}m</td>
                    <td>{Math.round(s.p90Seconds / 60)}m</td>
                    <td>{s.breaches}</td>
                    <td>
                      <Pill variant={s.percentOnTime >= 90 ? 'ok' : s.percentOnTime >= 75 ? 'warn' : 'err'}>
                        {s.percentOnTime}%
                      </Pill>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </article>
    </>
  );
}
